import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { auth, onAuthStateChanged } from "../components/firebase";
import { updateUser } from "../integrations/APIClient";
import {
  clearPendingGoogleAuth,
  formatAuthError,
  readPendingGoogleAuth,
  shouldUseGoogleRedirectFallback,
  storePendingGoogleAuth,
  syncBackendUser,
} from "../utils/authFlow";
import { getAuthReturnPath } from "../utils/authReturnPath";

export default function Register({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [chessLevel, setChessLevel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);
  const redirectSyncHandledRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(
    () => getAuthReturnPath(location.state?.from),
    [location.state]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setFirebaseUser(currentUser || null);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const pendingGoogleAuth = readPendingGoogleAuth();

    if (!pendingGoogleAuth || !firebaseUser || redirectSyncHandledRef.current) {
      return;
    }

    redirectSyncHandledRef.current = true;
    let cancelled = false;

    const finishRedirectRegistration = async () => {
      setLoading(true);
      setError(null);

      try {
        await syncBackendUser(firebaseUser);
        clearPendingGoogleAuth();

        if (cancelled) {
          return;
        }

        if (onSuccess) {
          onSuccess();
        } else if (location.pathname === "/register") {
          navigate(getAuthReturnPath(pendingGoogleAuth.redirectTo, redirectTo), { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            "Google sign-up finished, but we could not set up your account: " +
              formatAuthError(err, "Please try again.")
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    finishRedirectRegistration();

    return () => {
      cancelled = true;
    };
  }, [firebaseUser, location.pathname, navigate, onSuccess, redirectTo]);

  const shell = {
    maxWidth: 620,
    margin: "min(7vh, 48px) auto 0",
    color: "#f5ead0",
  };

  const card = {
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    borderRadius: 24,
    padding: "clamp(20px, 3vw, 30px)",
    background:
      "linear-gradient(180deg, rgba(12, 19, 38, 0.9), rgba(7, 12, 26, 0.96))",
    boxShadow:
      "0 0 0 1px rgba(255, 239, 183, 0.12), 0 20px 40px rgba(5, 10, 25, 0.35)",
  };

  const badge = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255, 213, 116, 0.72)",
    color: "#ffe8a3",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    marginBottom: 14,
  };

  const title = {
    color: "#fff1c9",
    fontWeight: 900,
    marginBottom: 8,
  };

  const subtitle = {
    color: "#eadfbe",
    lineHeight: 1.65,
    marginBottom: 18,
  };

  const input = {
    width: "100%",
    marginBottom: 12,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.35)",
    background: "rgba(7, 14, 28, 0.9)",
    color: "#fff8e8",
  };

  const row = {
    display: "flex",
    alignItems: "stretch",
    gap: 8,
    marginBottom: 12,
  };

  const btn = {
    width: "100%",
    padding: 12,
    fontWeight: 900,
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.78)",
    background:
      "linear-gradient(180deg, rgba(255, 213, 116, 0.96), rgba(232, 173, 52, 0.96))",
    color: "#241703",
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(5, 10, 25, 0.24)",
  };

  const toggleButton = {
    padding: "12px 14px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.35)",
    background: "rgba(255, 213, 116, 0.12)",
    color: "#fff1c9",
    cursor: "pointer",
    fontWeight: 700,
  };

  const footerText = {
    textAlign: "center",
    marginTop: 14,
    marginBottom: 0,
    color: "#d8cda8",
  };

  const footerLink = {
    color: "#ffe08b",
    fontWeight: 800,
    textDecoration: "none",
  };

  const divider = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "16px 0",
    color: "#d8cda8",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  const dividerLine = {
    flex: 1,
    height: 1,
    background: "rgba(255, 213, 116, 0.22)",
  };

  const googleButton = {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 14,
    border: "1.5px solid rgba(255, 213, 116, 0.46)",
    background: "rgba(255, 255, 255, 0.96)",
    color: "#1c1b1a",
    fontWeight: 800,
    boxShadow: "0 10px 24px rgba(5, 10, 25, 0.18)",
  };

  const googleIcon = {
    width: 18,
    height: 18,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    color: "#4285f4",
    background: "#fff",
  };

  const onChessLevelChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, "");
    setChessLevel(onlyDigits);
  };

  const onChessLevelBlur = () => {
    if (chessLevel === "") return;
    const n = Math.max(0, Math.min(3000, parseInt(chessLevel, 10) || 0));
    setChessLevel(String(n));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    try {
      setLoading(true);

      const { user } = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      const idToken = await user.getIdToken();
      clearPendingGoogleAuth();

      await updateUser({
        token: idToken,
        email: user.email,
        username: (username || email.split("@")[0]).trim(),
        fullName: (name || "").trim(),
        chessLevel: Math.max(0, Math.min(3000, parseInt(chessLevel, 10) || 0)),
      });

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(formatAuthError(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    setError(null);

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await signInWithPopup(auth, provider);
      clearPendingGoogleAuth();
      await syncBackendUser(result.user);

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      if (shouldUseGoogleRedirectFallback(err)) {
        storePendingGoogleAuth(redirectTo);
        await signInWithRedirect(auth, provider);
        return;
      }

      clearPendingGoogleAuth();
      setError(formatAuthError(err, "Google registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={shell}>
      <form onSubmit={handleRegister} style={card}>
        <div style={badge}>TRAINING REGISTER</div>
        <h2 style={title}>Create your training account</h2>
        <p style={subtitle}>
          Get 7 days of unlimited puzzle training free, starting as soon as you
          register. No card needed. After your free week, keep 7 free puzzles every
          day or choose £0.90/month for unlimited puzzles. Hood saves your progress
          and chooses positions for your level.
        </p>

        {error && <p style={{ color: "#ff9d9d", marginBottom: 12 }}>{error}</p>}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={input}
          autoComplete="email"
        />

        <div style={row}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            style={{ ...input, marginBottom: 0, flex: 1 }}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            style={toggleButton}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>

        <input
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={input}
          autoComplete="name"
        />

        <input
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          style={input}
          autoComplete="username"
        />

        <input
          placeholder="Chess Level (0-3000)"
          value={chessLevel}
          onChange={onChessLevelChange}
          onBlur={onChessLevelBlur}
          inputMode="numeric"
          style={{ ...input, marginBottom: 18 }}
        />

        <button type="submit" disabled={loading} style={btn}>
          {loading ? "Creating Account..." : "Register"}
        </button>

        <div style={divider}>
          <span style={dividerLine} />
          <span>or</span>
          <span style={dividerLine} />
        </div>

        <button
          type="button"
          disabled={loading}
          onClick={handleGoogleRegister}
          style={googleButton}
        >
          <span style={googleIcon}>G</span>
          {loading ? "Please wait..." : "Continue with Google"}
        </button>

        <p style={footerText}>
          Already have an account?{" "}
          <Link to="/login" state={{ from: location.state?.from }} style={footerLink}>
            Log in
          </Link>
        </p>
      </form>
    </div>
  );
}


